import { Pin } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Community } from '@/types';
import type { ViewMember, ViewMessage } from '../api/communityView';
import { fmtCount } from './channels';
import { avatarBg, avatarInitial, displayName, formatTime, priceLabel } from './cvHelpers';

type Scope = 'pinned' | 'members' | 'about';

const PANEL = 'no-scrollbar min-h-0 flex-1 overflow-y-auto px-7 pb-7 pt-[22px]';
const PANEL_TITLE = 'text-[22px] font-black tracking-[-0.5px] text-[#fafafa]';
const PANEL_SUB = 'mt-1 text-[13px] text-white/[0.72]';
const MEMBER_AV =
  'relative grid h-[42px] w-[42px] flex-none place-items-center rounded-full bg-white/10 bg-cover bg-center text-sm font-extrabold text-white';

interface Props {
  scope: Scope;
  community: Community;
  members: ViewMember[];
  pinnedMessages: ViewMessage[];
  onOpenProfile: (id: string, anchor?: DOMRect) => void;
  onOpenChannel: (id: string) => void;
}

export function CommunityPanels({
  scope,
  community,
  members,
  pinnedMessages,
  onOpenProfile,
}: Props) {
  if (scope === 'pinned') {
    return <Pinned pinned={pinnedMessages} />;
  }
  if (scope === 'members') {
    return <Roster members={members} onOpenProfile={onOpenProfile} />;
  }
  return <Overview community={community} members={members.length} />;
}

function Pinned({ pinned }: { pinned: ViewMessage[] }) {
  return (
    <div className={PANEL}>
      <div className="mb-[18px] flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Pin size={20} className="text-accent" />
            <div className={PANEL_TITLE}>Pinned</div>
          </div>
          <div className={PANEL_SUB}>Important posts and announcements saved by the team.</div>
        </div>
      </div>

      <div className="mb-[18px] grid grid-cols-3 gap-3.5">
        <StatTile num={pinned.length} label="Pinned posts" />
        <StatTile num={new Set(pinned.map((w) => w.author_id)).size} label="Contributors" />
        <StatTile num={0} label="Unread pins" />
      </div>

      {pinned.length ? (
        pinned.map((w) => {
          const name = displayName(w.author);
          return (
            <div className="mb-2.5 flex gap-3.5 rounded-md border border-white/[0.05] bg-white/[0.03] p-4" key={w.id}>
              <div className={MEMBER_AV} style={avatarBg(w.author?.avatar_url)}>
                {!w.author?.avatar_url && avatarInitial(name)}
              </div>
              <div className="min-w-0">
                <div>
                  <span className="font-bold text-[#fafafa]">{name}</span>{' '}
                  <span className="text-[11px] text-white/[0.46]">{formatTime(w.created_at)}</span>
                </div>
                <div className="text-sm leading-[1.55] text-white/[0.72]">{w.body}</div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="rounded-md border border-dashed border-white/10 bg-white/[0.02] p-12 text-center text-sm text-white/[0.46]">
          No pinned posts yet.
        </div>
      )}
    </div>
  );
}

function StatTile({ num, label }: { num: number; label: string }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.04] p-[18px]">
      <div className="text-[28px] font-black text-[#fafafa]">{num}</div>
      <div className="mt-0.5 text-xs text-white/[0.46]">{label}</div>
    </div>
  );
}

function Roster({
  members,
  onOpenProfile,
}: {
  members: ViewMember[];
  onOpenProfile: (id: string, anchor?: DOMRect) => void;
}) {
  return (
    <div className={PANEL}>
      <div className="mb-[18px] flex items-center justify-between">
        <div>
          <div className={PANEL_TITLE}>Members</div>
          <div className={PANEL_SUB}>{members.length} people building together</div>
        </div>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
        {members.map((m) => {
          const name = displayName(m.profile);
          return (
            <div
              className="flex cursor-pointer items-center gap-3 rounded-md border border-white/[0.05] bg-white/[0.04] p-3.5 transition-[background,border-color] duration-150 hover:border-white/10 hover:bg-white/[0.06]"
              key={m.user_id}
              onClick={(e) => onOpenProfile(m.user_id, e.currentTarget.getBoundingClientRect())}
            >
              <div className={MEMBER_AV} style={avatarBg(m.profile?.avatar_url)}>
                {!m.profile?.avatar_url && avatarInitial(name)}
                <span
                  className={cn(
                    'absolute -bottom-px -right-px h-[11px] w-[11px] rounded-full border-2 border-[#0a0a0e]',
                    m.online ? 'bg-[#22c55e]' : 'bg-[#6b7280]',
                  )}
                />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-bold text-[#fafafa]">{name}</div>
                <div className="mt-0.5 text-[11px] text-white/[0.46]">
                  {m.profile?.handle ? `@${m.profile.handle}` : 'member'} · {m.role}
                  {m.founding ? ' · ★' : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Overview({ community: c, members }: { community: Community; members: number }) {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const meta: [string, string][] = [
    ['Category', c.category || 'General'],
    ['Privacy', cap(c.privacy || 'public')],
    ['Access', c.price_cents > 0 ? priceLabel(c.price_cents) : 'Free'],
    ['Members', fmtCount(members)],
  ];
  return (
    <div className={cn(PANEL, 'max-w-[760px]')}>
      <div className="mb-[18px] flex items-center justify-between">
        <div>
          <div className={PANEL_TITLE}>About</div>
          <div className={PANEL_SUB}>What this community is about</div>
        </div>
      </div>
      <div className="mb-3.5 rounded-md border border-white/[0.05] bg-white/[0.04] p-6 text-[15px] leading-[1.7] text-white/[0.72]">
        {c.description || c.tagline || 'No description yet.'}
      </div>
      <div className="mb-3.5 grid grid-cols-2 gap-3.5">
        {meta.map(([k, v]) => (
          <div className="rounded-xl border border-white/[0.05] bg-white/[0.03] px-[18px] py-3.5" key={k}>
            <div className="mb-1 text-[11px] uppercase tracking-[0.6px] text-white/[0.46]">{k}</div>
            <div className="text-[15px] font-bold text-[#fafafa]">{v}</div>
          </div>
        ))}
      </div>
      {c.rules && (
        <div className="rounded-md border border-white/[0.05] bg-white/[0.03] p-5">
          <div className="mb-2.5 text-[11px] uppercase text-white/[0.46]">Ground rules</div>
          <div className="whitespace-pre-line text-sm leading-[1.7] text-white/[0.72]">{c.rules}</div>
        </div>
      )}
    </div>
  );
}
